import React, {useState, useEffect, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {LayoutPortal} from '../../LayoutPortal';
import {Spinner} from '../Spinner';
import {usePreviewContext} from '../../context/PreviewContext';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {YesNoModal} from '../elements/YesNoModal';
import {PortalConnectionCard} from './PortalConnectionCard';
import {usePortalClientId} from '../../hooks/usePortalClientId';
import {EscalafyChannelApi, payloadOf} from '../../api/escalafyChannelApi';
import {
    clearPendingPortalPick,
    ESCALAFY_FLOW_PORTAL,
    escalafyRedirectUri,
    initialPortalChoice,
    portalUnusableKey,
    readPendingPortalPick,
    setPendingEscalafyConnection,
} from '../client/economia/state/escalafyModel';
import {MercadoAdsErrorView} from '../channels/mercado_ads/MercadoAdsErrorView';
import {MercadoAdsSelectModal} from '../channels/mercado_ads/MercadoAdsSelectModal';
import {startMercadoAdsPortalAuth} from '../channels/mercado_ads/mercadoAdsApi';
import {
    ACTION_OTHER_ACCOUNT, ACTION_RETRY, FLOW_PORTAL, MERCADO_ADS_SLUG, consumeConnectResult,
} from '../channels/mercado_ads/mercadoAds';

// En el Portal el cliente vuelve a autorizar él mismo: «Enviar link al vendedor» es de la agencia, y el
// texto del colaborador le habla a él (el de la agencia le sugiere mandarle un link al vendedor).
const PORTAL_MERCADO_ADS_ACTIONS = [ACTION_RETRY, ACTION_OTHER_ACCOUNT];
const PORTAL_MERCADO_ADS_TEXTS = {
    MERCADO_ADS_NOT_MAIN_USER: 'portal.connections.mercado_ads.error.not_main_user',
    CONNECTOR_NOT_CONFIGURED: 'portal.connections.mercado_ads.error.not_configured',
};

const BASE = API_URL + 'portal/me/connections/';
const PENDING_CHANNEL_KEY = 'dashbo_portal_pending_channel';
const SHOPIFY_DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/;

// `mercado_ads` sólo llega si el backend lo habilita para la agencia del cliente (flag por agencia).
const ADS_SLUGS = ['google_ads', 'facebook', 'ga4', 'tiktok', 'linkedin', 'mercado_ads', 'gsc'];
const STORE_SLUGS = ['tienda_nube', 'shopify'];
// «Rentabilidad del negocio». Escalafy no vuelve a esta pantalla con `?code`: su `redirect_uri` es la ruta
// fija `/escalafy/callback`, que canjea el código y deja acá la lista de tiendas para elegir.
const ESCALAFY_SLUG = 'escalafy';
const PROFITABILITY_SLUGS = [ESCALAFY_SLUG];
const DESCRIPTION_KEY_BY_SLUG = {
    [ESCALAFY_SLUG]: 'portal_connections.escalafy_description',
    [MERCADO_ADS_SLUG]: 'portal.connections.mercado_ads.description',
};

// Las credenciales de Escalafy del dueño en ESTE cliente: puede haber conectado más de una cuenta, y desconectar
// tiene que revocarlas todas (la tarjeta dice «sin conectar» después).
const escalafyCredentialIds = (connection) => {
    const ids = [connection.credential_id, ...(connection.credentials || []).map((credential) => credential && credential.id)];
    return ids.filter((id, index) => id != null && ids.indexOf(id) === index);
};

const clearUrl = () => window.history.replaceState({}, '', window.location.pathname);

// El texto propio, nunca el `error_message` del backend: puede ser el `detail` en inglés de DRF, el
// relleno en voseo del `APIClient` o un texto que nombre a otro cliente (`CONNECTION_NOT_AVAILABLE`).
// Vale para todos los slugs (tanda «Mercado Ads como canal», 20/10: hasta ahí se mostraba crudo).
const connectErrorText = (t) => t('portal_connections.connect_error');

// Construye la URL de OAuth de Shopify para un dominio dado. Devuelve null si el dominio es inválido
// o falta la config. Compartida por el modal (input manual) y el auto-disparo cuando venimos de Shopify.
const buildShopifyOAuthUrl = (rawDomain) => {
    let domain = (rawDomain || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (!domain.includes('.myshopify.com')) domain = domain + '.myshopify.com';
    if (!SHOPIFY_DOMAIN_REGEX.test(domain)) return null;
    const template = process.env.REACT_APP_SHOPIFY_REDIRECT_URL || '';
    if (!template) return null;
    const subdomain = domain.split('.')[0];
    const redirectUri = window.location.origin + '/portal/connections';
    return template
        .replace('{{store_subdomain}}', subdomain)
        .replace('{{redirect_uri}}', encodeURIComponent(redirectUri));
};

/**
 * Pantalla del Portal del cliente para autogestionar SUS conexiones: ve solo las propias y las
 * conecta / reconecta / desconecta. El OAuth de ads/analytics/Tiendanube redirige y vuelve acá
 * (detectamos ?code); Shopify pide el shop domain y vuelve con shop+hmac+code.
 */
export const PortalConnections = () => {
    const {t} = useTranslation();
    const {isPreview} = usePreviewContext();
    // Cliente ACTIVO del portal (multi-cliente): scopea el listado y es el destino de toda
    // conexión nueva (con N clientes el backend lo exige explícito — MISSING_CLIENT sin él).
    const activeClientId = usePortalClientId();
    const [connections, setConnections] = useState(null);
    const [busySlug, setBusySlug] = useState(null);
    const [disconnectTarget, setDisconnectTarget] = useState(null);
    const [shopifyModalOpen, setShopifyModalOpen] = useState(false);
    const [shopifyInput, setShopifyInput] = useState('');
    // true mientras procesamos el callback de Shopify (token-exchange + get_store contra la tienda):
    // es la espera inevitable que depende de Shopify; mostramos un mensaje claro en vez de un spinner mudo.
    const [connecting, setConnecting] = useState(false);
    // La vuelta de Escalafy: las tiendas que ve el token, para que el dueño elija UNA (`null` = cerrado).
    const [escalafyPick, setEscalafyPick] = useState(null);
    const [pickChoice, setPickChoice] = useState(null);
    const [sharing, setSharing] = useState(false);
    // Mercado Ads vuelve por su ruta fija (`/connect-mercado-ads-account`), que canjea el código y deja
    // acá el resultado: la elección de anunciantes (`null` = cerrada) o el error, en su tarjeta.
    const [mercadoAdsPick, setMercadoAdsPick] = useState(null);
    // La elección que se cerró con la X sin compartir nada: la conexión quedó hecha pero la agencia no ve
    // ninguna cuenta. La tarjeta lo dice y la vuelve a abrir.
    const [mercadoAdsPendingPick, setMercadoAdsPendingPick] = useState(null);
    const [mercadoAdsError, setMercadoAdsError] = useState(null);
    const [mercadoAdsAction, setMercadoAdsAction] = useState(null);

    const fetchConnections = useCallback(() => {
        const suffix = activeClientId ? `?client_id=${encodeURIComponent(activeClientId)}` : '';
        new APIClient(BASE + suffix).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                setConnections(data.connections);
            } else {
                setConnections({});
                toast.error(t('portal_connections.load_error'));
            }
        });
    }, [t, activeClientId]);

    // Al montar: detectar si volvemos de un OAuth (?code, o ?shop+hmac para Shopify) y procesar el callback.
    useEffect(() => {
        if (isPreview) {
            fetchConnections();
            return;
        }
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const shop = params.get('shop');
        const pendingChannel = localStorage.getItem(PENDING_CHANNEL_KEY);

        const afterCallback = (data) => {
            setConnecting(false);
            // Solo 'OK' es éxito: un fallo de red (NETWORK_ERROR) NO debe reportarse como conectado.
            if (data && data.status === 'OK') {
                toast.success(t('portal_connections.connected_ok'));
            } else {
                toast.error(connectErrorText(t));
            }
            fetchConnections();
        };

        if (code && shop) {
            localStorage.removeItem(PENDING_CHANNEL_KEY);
            const payload = {
                authorization_code: code,
                shop: shop,
                hmac: params.get('hmac'),
                state: params.get('state'),
                all_params: Object.fromEntries(params.entries()),
            };
            if (activeClientId) payload.agency_client_id = activeClientId;
            clearUrl();
            setConnecting(true);
            new APIClient(BASE + 'shopify/callback/').call('POST', payload).then(afterCallback);
        } else if (code && pendingChannel) {
            localStorage.removeItem(PENDING_CHANNEL_KEY);
            clearUrl();
            setConnecting(true);
            const callbackPayload = {code};
            if (activeClientId) callbackPayload.agency_client_id = activeClientId;
            new APIClient(BASE + pendingChannel + '/callback/').call('POST', callbackPayload).then(afterCallback);
        } else if (shop) {
            // Viene del onboarding con código (ConnectWithCode arrastra el `shop` de la instalación de
            // Shopify): ya sabemos la tienda, así que disparamos el OAuth directo, sin pedirle que
            // reingrese/confirme el dominio en el modal. Si por algún motivo no se puede armar la URL,
            // caemos a mostrar el portal normalmente.
            clearUrl();
            const url = buildShopifyOAuthUrl(shop);
            if (url) {
                window.location.href = url;
            } else {
                fetchConnections();
            }
        } else {
            fetchConnections();
        }
    }, [isPreview, fetchConnections, t, activeClientId]);

    // La vuelta de Mercado Ads: con más de un anunciante, el cliente elige cuáles compartir; con uno,
    // el backend ya lo vinculó. Un error se queda en la tarjeta.
    useEffect(() => {
        if (isPreview) return;
        const result = consumeConnectResult(FLOW_PORTAL);
        if (!result) return;
        if (result.status !== 'OK') {
            setMercadoAdsError({code: result.status, nickname: result.nickname || null});
            return;
        }
        if (result.needsSelection && result.advertisers.length > 1) {
            setMercadoAdsPick(result);
            return;
        }
        toast.success(t('portal_connections.connected_ok'));
    }, [isPreview, t]);

    useEffect(() => {
        if (isPreview) return;
        const pick = readPendingPortalPick();
        if (!pick) return;
        setEscalafyPick(pick);
        setPickChoice(initialPortalChoice(pick.organizations));
    }, [isPreview]);

    const closeEscalafyPick = () => {
        clearPendingPortalPick();
        setEscalafyPick(null);
        setPickChoice(null);
        fetchConnections();
    };

    const shareEscalafyStore = () => {
        if (!escalafyPick || !pickChoice || sharing) return;
        const org = escalafyPick.organizations.find((item) => String(item.org_id) === pickChoice);
        if (!org) return;
        setSharing(true);
        EscalafyChannelApi.portalSelect({
            credentialId: escalafyPick.credentialId,
            orgId: org.org_id,
            agencyClientId: escalafyPick.agencyClientId || activeClientId,
        }).then((response) => {
            setSharing(false);
            const data = payloadOf(response);
            if (!data) {
                // Mensaje propio: `CONNECTION_NOT_AVAILABLE` no dice si la tienda la tiene otro cliente o la agencia.
                toast.error(t('portal_connections.connect_error'));
                return;
            }
            toast.success(t(data.upgraded === true
                ? 'portal_connections.escalafy_upgraded'
                : 'portal_connections.escalafy_shared_ok'));
            closeEscalafyPick();
        }).catch(() => {
            setSharing(false);
            toast.error(t('portal_connections.connect_error'));
        });
    };

    const connectEscalafy = () => {
        setBusySlug(ESCALAFY_SLUG);
        const failed = () => {
            setBusySlug(null);
            toast.error(t('portal_connections.connect_error'));
        };
        EscalafyChannelApi.portalAuthUrl(escalafyRedirectUri(), activeClientId).then((response) => {
            const data = payloadOf(response);
            const url = data && (data.auth_url || data.url);
            if (!url) {
                failed();
                return;
            }
            setPendingEscalafyConnection({flow: ESCALAFY_FLOW_PORTAL, agencyClientId: activeClientId});
            window.location.assign(url);
        }).catch(failed);
    };

    // Conectar desde la tarjeta, o reintentar desde su error (`action`): el error queda a la vista,
    // con el botón ocupado, hasta que se va a Mercado Libre o vuelve a fallar.
    const connectMercadoAds = (action = null) => {
        setBusySlug(MERCADO_ADS_SLUG);
        setMercadoAdsAction(action);
        if (!action) setMercadoAdsError(null);
        startMercadoAdsPortalAuth(activeClientId).then((result) => {
            if (result.ok) return;
            setBusySlug(null);
            setMercadoAdsAction(null);
            setMercadoAdsError({code: result.code, nickname: null});
        });
    };

    const closeMercadoAdsPick = (shared) => {
        const pick = mercadoAdsPick;
        setMercadoAdsPick(null);
        const nothingShared = !shared && pick && !(pick.linkedAdvertiserIds || []).length;
        setMercadoAdsPendingPick(nothingShared ? pick : null);
        if (shared) toast.success(t('portal_connections.connected_ok'));
        fetchConnections();
    };

    const reopenMercadoAdsPick = () => {
        setMercadoAdsPick(mercadoAdsPendingPick);
        setMercadoAdsPendingPick(null);
    };

    const submitShopifyDomain = () => {
        const url = buildShopifyOAuthUrl(shopifyInput);
        if (!url) {
            toast.error(t('portal_connections.shopify_invalid'));
            return;
        }
        window.location.href = url;
    };

    const handleConnect = (slug) => {
        if (isPreview) return;
        if (slug === 'shopify') {
            setShopifyInput('');
            setShopifyModalOpen(true);
            return;
        }
        if (slug === ESCALAFY_SLUG) {
            connectEscalafy();
            return;
        }
        if (slug === MERCADO_ADS_SLUG) {
            connectMercadoAds();
            return;
        }
        setBusySlug(slug);
        localStorage.setItem(PENDING_CHANNEL_KEY, slug);
        const origin = encodeURIComponent(window.location.origin);
        const clientParam = activeClientId ? '&agency_client_id=' + encodeURIComponent(activeClientId) : '';
        new APIClient(BASE + slug + '/auth-url/?origin=' + origin + clientParam).call('GET').then((data) => {
            if (data && data.auth_url) {
                window.location = data.auth_url;
            } else {
                setBusySlug(null);
                localStorage.removeItem(PENDING_CHANNEL_KEY);
                toast.error(connectErrorText(t));
            }
        });
    };

    const confirmDisconnect = () => {
        const {slug, connection} = disconnectTarget;
        setBusySlug(slug);
        setDisconnectTarget(null);
        const finish = (data) => {
            setBusySlug(null);
            // Solo 'OK' es éxito: un fallo de red (NETWORK_ERROR) NO debe reportarse como desconectado.
            if (data && data.status === 'OK') {
                toast.success(t('portal_connections.disconnected_ok'));
            } else {
                toast.error(t('portal_connections.disconnect_error'));
            }
            fetchConnections();
        };
        // Escalafy y Mercado Ads: el cliente puede haber conectado más de una cuenta (Mercado Ads, un vendedor
        // por cuenta), y desconectar tiene que revocarlas todas. Mercado Ads exige `credential_id`: sin él,
        // con dos vendedores no se desconectaba ninguno.
        if (slug === ESCALAFY_SLUG || slug === MERCADO_ADS_SLUG) {
            // Revoca cada token del dueño y desvincula lo que ESTE cliente compartió: un POST por credencial, en
            // serie, y se corta en el primero que falla.
            const post = (credentialId) => {
                const payload = credentialId != null ? {credential_id: credentialId} : {};
                if (activeClientId) payload.agency_client_id = activeClientId;
                return new APIClient(BASE + slug + '/disconnect/').call('POST', payload);
            };
            const ids = escalafyCredentialIds(connection);
            (ids.length ? ids : [null])
                .reduce((chain, id) => chain.then((previous) => (
                    previous && previous.status !== 'OK' ? previous : post(id))), Promise.resolve(null))
                // El texto propio, nunca el `error_message` del backend: viene en español fijo («No encontrado»)
                // o es el `detail` de DRF / el relleno del `APIClient`. Conectar y compartir hacen lo mismo.
                .then((data) => finish(data && data.status === 'OK' ? data : {status: 'ERROR'}))
                .catch(() => finish({status: 'ERROR'}));
            return;
        }
        const payload = {};
        if (connection.kind === 'store' && connection.stores && connection.stores[0]) {
            payload.ad_account_id = connection.stores[0].ad_account_id;
        } else if (connection.credentials && connection.credentials.length === 1) {
            payload.credential_id = connection.credentials[0].id;
        }
        new APIClient(BASE + slug + '/disconnect/').call('POST', payload).then(finish);
    };

    const renderSection = (titleKey, slugs) => {
        const visible = slugs.filter(slug => connections[slug]);
        if (visible.length === 0) return null;
        return (
            <section className="portal-connections__group">
                <div className="portal-connections__group-head">
                    <h2>{t(titleKey)}</h2>
                    <span className="portal-connections__count">{visible.length}</span>
                    <span className="portal-connections__rule" aria-hidden="true"/>
                </div>
                <div className="portal-connections__grid">
                    {visible.map((slug, i) => (
                        <PortalConnectionCard
                            key={slug}
                            slug={slug}
                            index={i}
                            connection={connections[slug]}
                            description={DESCRIPTION_KEY_BY_SLUG[slug] ? t(DESCRIPTION_KEY_BY_SLUG[slug]) : null}
                            busy={busySlug === slug || isPreview}
                            onConnect={() => handleConnect(slug)}
                            onDisconnect={() => setDisconnectTarget({slug, connection: connections[slug]})}
                            notice={slug !== MERCADO_ADS_SLUG ? null : mercadoAdsError ? (
                                <MercadoAdsErrorView code={mercadoAdsError.code} nickname={mercadoAdsError.nickname}
                                                     allowedActions={PORTAL_MERCADO_ADS_ACTIONS}
                                                     textKeys={PORTAL_MERCADO_ADS_TEXTS}
                                                     onAction={connectMercadoAds} busyAction={mercadoAdsAction}/>
                            ) : mercadoAdsPendingPick ? (
                                <span className="ma-pending" data-testid="mercado-ads-pending-pick">
                                    <span className="ma-pending__text">{t('portal.connections.mercado_ads.pending_pick')}</span>
                                    <button type="button" className="portal-connection-card__btn -primary"
                                            onClick={reopenMercadoAdsPick}>
                                        {t('portal.connections.mercado_ads.pending_pick_action')}
                                    </button>
                                </span>
                            ) : null}
                        />
                    ))}
                </div>
            </section>
        );
    };

    return (
        <LayoutPortal>
            <div className="portal-embedded-page portal-connections">
                <header className="portal-connections__header">
                    <h1>{t('portal_connections.title')}</h1>
                    <p>{t('portal_connections.subtitle')}</p>
                </header>

                {(connecting || connections === null) ? (
                    <div className="box-loader" style={{flexDirection: 'column', gap: '0.9em'}}>
                        <Spinner color="positive"/>
                        <p style={{color: '#4A4A4A', margin: 0, textAlign: 'center'}}>
                            {connecting
                                ? t('portal_connections.connecting_shopify')
                                : t('portal_connections.loading')}
                        </p>
                    </div>
                ) : (
                    <>
                        {renderSection('portal_connections.platforms', ADS_SLUGS)}
                        {renderSection('portal_connections.stores', STORE_SLUGS)}
                        {renderSection('portal_connections.profitability', PROFITABILITY_SLUGS)}
                    </>
                )}
            </div>

            {disconnectTarget && (
                <YesNoModal
                    isOpen={true}
                    title={t('portal_connections.disconnect_confirm_title')}
                    question={t('portal_connections.disconnect_confirm_question', {name: disconnectTarget.connection.name})}
                    onYesAnswer={confirmDisconnect}
                    onNoAnswer={() => setDisconnectTarget(null)}
                />
            )}

            {/* `static`: un clic afuera no descarta la lista (habría que repetir el OAuth). Cierran la X y Escape. */}
            <Modal isOpen={!!escalafyPick} toggle={closeEscalafyPick} backdrop="static" className="portal-escalafy-pick-modal">
                <ModalHeader toggle={closeEscalafyPick}>
                    {t('portal_connections.escalafy_choose_org')}
                </ModalHeader>
                <ModalBody>
                    <div className="modal-section portal-escalafy-pick" data-testid="portal-escalafy-pick">
                        <p className="portal-escalafy-pick__hint">{t('portal_connections.escalafy_choose_org_hint')}</p>
                        <ul className="portal-escalafy-pick__orgs">
                            {(escalafyPick ? escalafyPick.organizations : []).map((org) => {
                                const unusableKey = portalUnusableKey(org);
                                const id = String(org.org_id);
                                return (
                                    <li key={id}>
                                        <label className={unusableKey ? 'portal-escalafy-pick__org -off' : 'portal-escalafy-pick__org'}
                                               data-testid={'portal-escalafy-org-' + id}>
                                            <input type="radio" name="portal-escalafy-org" value={id}
                                                   disabled={!!unusableKey} checked={pickChoice === id}
                                                   onChange={() => setPickChoice(id)}/>
                                            <span className="portal-escalafy-pick__name">{org.name}</span>
                                            {unusableKey && (
                                                <span className="portal-escalafy-pick__why">{t(unusableKey)}</span>
                                            )}
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button className="btn -alt" disabled={!pickChoice || sharing} onClick={shareEscalafyStore}
                            aria-busy={sharing || undefined} data-testid="portal-escalafy-share">
                        {sharing && <Spinner color="negative very-small"/>}
                        {t('portal_connections.escalafy_share')}
                    </button>
                </ModalFooter>
            </Modal>

            <MercadoAdsSelectModal result={mercadoAdsPick} activeClientId={activeClientId}
                                   onClose={closeMercadoAdsPick}/>

            <Modal isOpen={shopifyModalOpen} toggle={() => setShopifyModalOpen(false)}>
                <ModalHeader toggle={() => setShopifyModalOpen(false)}>
                    {t('portal_connections.shopify_modal_title')}
                </ModalHeader>
                <ModalBody>
                    <div className="modal-section">
                        <label>{t('portal_connections.shopify_prompt')}</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="mitienda.myshopify.com"
                            value={shopifyInput}
                            onChange={(e) => setShopifyInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submitShopifyDomain(); }}
                            autoFocus
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button className="btn -alt" disabled={!shopifyInput.trim()} onClick={submitShopifyDomain}>
                        {t('portal_connections.connect')}
                    </button>
                </ModalFooter>
            </Modal>
        </LayoutPortal>
    );
};
